import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { Metadata, CompositeKey, RunView } from '@memberjunction/core';
import { NavigationService } from '@memberjunction/ng-shared';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BCSaaSOrganizationPersonRoleEntity, BCSaaSOrganizationRoleEntity, BCSaaSPlanEntity, BCSaaSPlanPriceEntity, BCSaaSSubscriptionEntity, BCSaaSOrganizationConfigEntity } from '@bluecypress/bcsaas-entities';
import { BCMembershipService } from '@bluecypress/bcsaas-entities-extended';
import { mjBizAppsCommonOrganizationEntity } from '@mj-biz-apps/common-entities';
import { OrganizationEntityExtended, IzzyMetadataEngine } from 'mj_generatedentities';
import { IzzyAuthService } from '../../services/izzy-auth.service';

/**
 * Tree node for hierarchical organization display
 */
interface OrgTreeNode {
  org: mjBizAppsCommonOrganizationEntity;
  children: OrgTreeNode[];
  expanded: boolean;
  depth: number;
  hasChildren: boolean;
}

/**
 * State interface for persisting component state
 */
interface OrganizationsComponentState {
  selectedOrganizationId?: string;
  searchQuery?: string;
  sortBy?: 'name' | 'status';
  sortDirection?: 'asc' | 'desc';
  expandedOrgIds?: string[];
}

/**
 * Interface for interaction events emitted by the component
 */
interface OrganizationsInteraction {
  type: 'selectOrganization' | 'createOrganization' | 'editOrganization' | 'deleteOrganization';
  organizationId?: string;
}

/**
 * Interface for query params
 */
interface OrganizationsQueryParams {
  organizationId?: string;
}

/**
 * Organizations Component
 *
 * Manages organizations with:
 * - Organization list with search and sort
 * - Add/Edit/Delete operations
 * - Quick access to organization settings
 * - Organization details panel
 */
@Component({
  standalone: false,
  selector: 'izzy-organizations',
  templateUrl: './organizations.component.html',
  styleUrls: ['./organizations.component.css']
})
export class OrganizationsComponent implements OnInit, OnDestroy {

  // ========== Inputs/Outputs ==========
  @Input() initialState: OrganizationsComponentState | null = null;
  @Output() stateChange = new EventEmitter<OrganizationsComponentState>();
  @Output() openRecord = new EventEmitter<{ EntityName: string; RecordID: string }>();
  @Output() interaction = new EventEmitter<OrganizationsInteraction>();
  @Output() settingsNavigation = new EventEmitter<string>();

  /** Emitted when organizations are created, updated, or deleted */
  @Output() organizationsChanged = new EventEmitter<void>();

  constructor(
    private navigationService: NavigationService,
    private location: Location,
    private route: ActivatedRoute,
    private authService: IzzyAuthService
  ) {}

  // ========== Data ==========
  protected organizations: mjBizAppsCommonOrganizationEntity[] = [];
  protected orgTree: OrgTreeNode[] = [];

  // ========== Selection State ==========
  protected selectedOrganization: mjBizAppsCommonOrganizationEntity | null = null;

  // ========== View Mode ==========
  protected viewMode: 'tree' | 'flat' = 'tree';

  // ========== UI State ==========
  protected isLoading: boolean = true;
  protected isSaving: boolean = false;
  protected errorMessage: string = '';
  protected successMessage: string = '';

  // ========== Search & Sort ==========
  protected searchQuery: string = '';
  protected sortBy: 'name' | 'status' = 'name';
  protected sortDirection: 'asc' | 'desc' = 'asc';

  // ========== Edit Mode ==========
  protected isEditing: boolean = false;
  protected isCreating: boolean = false;
  protected editForm: {
    name: string;
    description: string;
    parentId: string | null;
    logoURL: string | null;
  } = { name: '', description: '', parentId: null, logoURL: null };
  protected logoPreviewError: boolean = false;

  // ========== Plan assignment (restricted) ==========
  /** All active plans (the catalog) for the dropdown. */
  protected plans: BCSaaSPlanEntity[] = [];
  /** The plan currently resolved for the selected org (via Active Subscription -> PlanPrice -> Plan), or null. */
  protected currentPlan: BCSaaSPlanEntity | null = null;
  /** The plan ID chosen in the dropdown to assign. */
  protected selectedPlanId: string | null = null;
  protected isSavingPlan: boolean = false;
  protected planMessage: string = '';
  /** Recurring pricing model — the default for an auto-created PlanPrice. */
  private static readonly RECURRING_PRICING_MODEL_ID = '8b31f541-5c13-4822-b539-fa3fb659da3d';

  // ========== Confirm Dialog ==========
  protected confirmDialogVisible: boolean = false;
  protected confirmDialogTitle: string = '';
  protected confirmDialogMessage: string = '';
  protected confirmDialogAction: (() => Promise<void>) | null = null;

  // ========== Cleanup ==========
  private destroy$ = new Subject<void>();

  private _pendingOrgId: string | null = null;

  async ngOnInit(): Promise<void> {
    // Subscribe to query param changes for deep linking
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.handleQueryParams(params);
    });

    await this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle query params for deep linking
   */
  private handleQueryParams(params: OrganizationsQueryParams): void {
    if (params.organizationId) {
      this._pendingOrgId = params.organizationId;
    }

    // If data is already loaded, apply immediately
    if (!this.isLoading && this.organizations.length > 0) {
      this.applyPendingSelections();
    }
  }

  /**
   * Apply pending selections from query params
   */
  private applyPendingSelections(): void {
    if (this._pendingOrgId) {
      const org = this.organizations.find(o => o.ID === this._pendingOrgId);
      if (org && org.ID !== this.selectedOrganization?.ID) {
        this.selectOrganization(org);
      }
      this._pendingOrgId = null;
    }
  }

  /**
   * Update URL query params without full navigation
   */
  private updateQueryParams(): void {
    const params = new URLSearchParams();

    if (this.selectedOrganization) {
      params.set('organizationId', this.selectedOrganization.ID);
    }

    // Get current path and update only query string
    const path = this.location.path().split('?')[0];
    const queryString = params.toString();
    const newUrl = queryString ? `${path}?${queryString}` : path;
    this.location.replaceState(newUrl);
  }

  protected async loadData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      // Initialize IzzyMetadataEngine (does nothing if already initialized)
      await IzzyMetadataEngine.Instance.Config(false);
      const engine = IzzyMetadataEngine.Instance;
      const currentContactId = engine.CurrentContactID;

      // Check if user is a system developer (has access to internal org)
      const isSystemDev = await this.authService.isSystemDeveloper();

      if (isSystemDev) {
        // System developers can see all organizations
        this.organizations = engine.Organizations;
      } else if (currentContactId) {
        // Regular users only see their organizations (v2.3 - Security)
        this.organizations = engine.GetUserOrganizations(currentContactId);

        if (this.organizations.length === 0) {
          this.errorMessage = 'You do not have access to any organizations.';
        }
      } else {
        // No contact loaded - no access
        this.organizations = [];
        this.errorMessage = 'Unable to load user information. Please contact support.';
      }

      // Build the tree structure
      this.buildOrgTree();

      // Clear selection if selected org no longer exists
      if (this.selectedOrganization && !this.organizations.find(o => o.ID === this.selectedOrganization!.ID)) {
        this.selectedOrganization = null;
      }

      // Apply pending selections from query params, or auto-select defaults
      if (this._pendingOrgId) {
        this.applyPendingSelections();
      } else if (!this.selectedOrganization && this.organizations.length > 0) {
        // Auto-select first root org if none selected
        if (this.orgTree.length > 0) {
          this.selectOrganization(this.orgTree[0].org);
        } else {
          this.selectOrganization(this.organizations[0]);
        }
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
      this.errorMessage = 'Failed to load organizations. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  // ========== Tree Building ==========

  /**
   * Build the hierarchical tree structure from flat organizations list
   */
  private buildOrgTree(): void {
    const orgMap = new Map<string, OrgTreeNode>();

    // First pass: create nodes for all orgs
    for (const org of this.organizations) {
      orgMap.set(org.ID, {
        org,
        children: [],
        expanded: true, // Default expanded
        depth: 0,
        hasChildren: false
      });
    }

    // Second pass: build parent-child relationships
    const rootNodes: OrgTreeNode[] = [];
    for (const org of this.organizations) {
      const node = orgMap.get(org.ID)!;
      const parentId = org.ParentID;

      if (parentId && orgMap.has(parentId)) {
        const parentNode = orgMap.get(parentId)!;
        parentNode.children.push(node);
        parentNode.hasChildren = true;
      } else {
        rootNodes.push(node);
      }
    }

    // Third pass: calculate depths
    const setDepths = (nodes: OrgTreeNode[], depth: number) => {
      for (const node of nodes) {
        node.depth = depth;
        setDepths(node.children, depth + 1);
      }
    };
    setDepths(rootNodes, 0);

    // Sort children by name at each level
    const sortChildren = (nodes: OrgTreeNode[]) => {
      nodes.sort((a, b) => a.org.Name.localeCompare(b.org.Name));
      for (const node of nodes) {
        sortChildren(node.children);
      }
    };
    sortChildren(rootNodes);

    this.orgTree = rootNodes;
  }

  /**
   * Toggle expand/collapse for a tree node
   */
  toggleExpand(node: OrgTreeNode, event: Event): void {
    event.stopPropagation();
    node.expanded = !node.expanded;
  }

  /**
   * Expand all nodes in the tree
   */
  expandAll(): void {
    const setExpanded = (nodes: OrgTreeNode[], expanded: boolean) => {
      for (const node of nodes) {
        node.expanded = expanded;
        setExpanded(node.children, expanded);
      }
    };
    setExpanded(this.orgTree, true);
  }

  /**
   * Collapse all nodes in the tree
   */
  collapseAll(): void {
    const setExpanded = (nodes: OrgTreeNode[], expanded: boolean) => {
      for (const node of nodes) {
        node.expanded = expanded;
        setExpanded(node.children, expanded);
      }
    };
    setExpanded(this.orgTree, false);
  }

  /**
   * Get flattened list of visible tree nodes (for rendering)
   */
  get flattenedTreeNodes(): OrgTreeNode[] {
    const result: OrgTreeNode[] = [];
    const flatten = (nodes: OrgTreeNode[]) => {
      for (const node of nodes) {
        result.push(node);
        if (node.expanded && node.children.length > 0) {
          flatten(node.children);
        }
      }
    };
    flatten(this.orgTree);
    return result;
  }

  /**
   * Check if a node is the last child at its level
   */
  isLastChild(node: OrgTreeNode): boolean {
    const parentId = node.org.ParentID;
    if (!parentId) {
      // Root level - check against root nodes
      const rootNodes = this.orgTree;
      return rootNodes[rootNodes.length - 1]?.org.ID === node.org.ID;
    }
    // Find parent and check if this is the last child
    const findParent = (nodes: OrgTreeNode[]): OrgTreeNode | null => {
      for (const n of nodes) {
        if (n.org.ID === parentId) return n;
        const found = findParent(n.children);
        if (found) return found;
      }
      return null;
    };
    const parent = findParent(this.orgTree);
    if (!parent) return true;
    return parent.children[parent.children.length - 1]?.org.ID === node.org.ID;
  }

  /**
   * Get count of all descendants for an org
   */
  getDescendantCount(node: OrgTreeNode): number {
    let count = 0;
    const countDescendants = (n: OrgTreeNode) => {
      count += n.children.length;
      for (const child of n.children) {
        countDescendants(child);
      }
    };
    countDescendants(node);
    return count;
  }

  /**
   * Get the hierarchy path (breadcrumb) for an organization
   */
  getOrgPath(org: mjBizAppsCommonOrganizationEntity): mjBizAppsCommonOrganizationEntity[] {
    const path: mjBizAppsCommonOrganizationEntity[] = [];
    let current: mjBizAppsCommonOrganizationEntity | undefined = org;

    while (current) {
      path.unshift(current);
      const parentId = current.ParentID;
      current = parentId ? this.organizations.find(o => o.ID === parentId) : undefined;
    }

    return path;
  }

  /**
   * Get potential parent organizations for the edit form
   * (excludes self and descendants to prevent circular references)
   */
  getPotentialParents(): mjBizAppsCommonOrganizationEntity[] {
    if (!this.isEditing || !this.selectedOrganization) {
      // Creating new - all orgs can be parents
      return this.organizations;
    }

    // Editing - exclude self and all descendants
    const excludeIds = new Set<string>();
    excludeIds.add(this.selectedOrganization.ID);

    const addDescendants = (orgId: string) => {
      for (const org of this.organizations) {
        if (org.ParentID === orgId) {
          excludeIds.add(org.ID);
          addDescendants(org.ID);
        }
      }
    };
    addDescendants(this.selectedOrganization.ID);

    return this.organizations.filter(o => !excludeIds.has(o.ID));
  }

  /**
   * Get the tree structure for parent selector dropdown
   */
  getParentSelectorTree(): OrgTreeNode[] {
    const potentialParents = new Set(this.getPotentialParents().map(o => o.ID));

    // Build filtered tree only showing potential parents
    const filterTree = (nodes: OrgTreeNode[]): OrgTreeNode[] => {
      const result: OrgTreeNode[] = [];
      for (const node of nodes) {
        if (potentialParents.has(node.org.ID)) {
          result.push({
            ...node,
            children: filterTree(node.children)
          });
        } else {
          // Check if any descendants are potential parents
          const filteredChildren = filterTree(node.children);
          if (filteredChildren.length > 0) {
            result.push(...filteredChildren);
          }
        }
      }
      return result;
    };

    return filterTree(this.orgTree);
  }

  // ========== Organization Selection ==========

  selectOrganization(org: mjBizAppsCommonOrganizationEntity): void {
    this.selectedOrganization = org;
    this.isEditing = false;
    this.isCreating = false;
    this.planMessage = '';
    this.updateQueryParams();
    // Fire-and-forget: load this org's current plan for the Plan section.
    void this.loadCurrentPlan(org.ID);
  }

  // ========== Plan assignment (restricted) ==========

  /** Load the active plan catalog for the dropdown (once). */
  private async loadPlans(): Promise<void> {
    try {
      const rv = new RunView();
      const res = await rv.RunView<BCSaaSPlanEntity>({
        EntityName: 'BC: Plans',
        ExtraFilter: `Status = 'Active'`,
        OrderBy: '[Level] ASC',
        ResultType: 'entity_object',
      });
      this.plans = res.Success ? res.Results : [];
    } catch (e) {
      console.error('Organizations: loadPlans failed', e);
      this.plans = [];
    }
  }

  /**
   * Resolve the org's current plan the SAME way the runtime gate does:
   * Active/Trialing Subscription -> PlanPrice -> Plan. (See
   * IzzyMetadataEngine.GetOrganizationPlan.) Sets currentPlan + seeds the dropdown.
   */
  private async loadCurrentPlan(orgId: string): Promise<void> {
    this.currentPlan = null;
    this.selectedPlanId = null;
    try {
      if (this.plans.length === 0) { await this.loadPlans(); }
      const rv = new RunView();
      const subRes = await rv.RunView<BCSaaSSubscriptionEntity>({
        EntityName: 'BC: Subscriptions',
        ExtraFilter: `OrganizationID = '${orgId}' AND (Status = 'Active' OR Status = 'Trialing')`,
        ResultType: 'entity_object',
      });
      const sub = (subRes.Success ? subRes.Results : [])[0];
      if (sub?.PlanPriceID) {
        const ppRes = await rv.RunView<BCSaaSPlanPriceEntity>({
          EntityName: 'BC: Plan Prices',
          ExtraFilter: `ID = '${sub.PlanPriceID}'`,
          ResultType: 'entity_object',
        });
        const planId = (ppRes.Success ? ppRes.Results : [])[0]?.PlanID;
        if (planId) {
          this.currentPlan = this.plans.find(p => p.ID === planId) ?? null;
        }
      }
      this.selectedPlanId = this.currentPlan?.ID ?? null;
    } catch (e) {
      console.error('Organizations: loadCurrentPlan failed', e);
    }
  }

  /**
   * Assign the selected plan to the selected org. Writes the chain the gate reads:
   *   find-or-create PlanPrice for the plan -> upsert Active Subscription (org -> PlanPrice)
   *   -> set OrganizationConfig.PlanID (the second resolution path).
   * All via the entity layer (auditable), matching the other restricted admin screens.
   */
  protected async setPlan(): Promise<void> {
    const org = this.selectedOrganization;
    if (!org || !this.selectedPlanId) { return; }
    this.isSavingPlan = true;
    this.planMessage = '';
    try {
      const md = new Metadata();
      const rv = new RunView();
      const planId = this.selectedPlanId;

      // 1. Find-or-create a PlanPrice for this plan (Recurring model). The gate only needs
      //    Subscription -> PlanPrice -> Plan to resolve; price details are not material here.
      let planPriceId: string;
      const ppRes = await rv.RunView<BCSaaSPlanPriceEntity>({
        EntityName: 'BC: Plan Prices',
        ExtraFilter: `PlanID = '${planId}'`,
        ResultType: 'entity_object',
      });
      const existingPP = (ppRes.Success ? ppRes.Results : [])[0];
      if (existingPP) {
        planPriceId = existingPP.ID;
      } else {
        const pp = await md.GetEntityObject<BCSaaSPlanPriceEntity>('BC: Plan Prices');
        pp.NewRecord();
        pp.PlanID = planId;
        pp.PricingModelID = OrganizationsComponent.RECURRING_PRICING_MODEL_ID;
        pp.Currency = 'USD';
        pp.Price = 0;
        pp.IsActive = true;
        if (!await pp.Save()) {
          throw new Error(`Failed to create PlanPrice: ${pp.LatestResult?.Message ?? 'unknown'}`);
        }
        planPriceId = pp.ID;
      }

      // 2. Upsert the org's Subscription (the path GetOrganizationPlan reads).
      const subRes = await rv.RunView<BCSaaSSubscriptionEntity>({
        EntityName: 'BC: Subscriptions',
        ExtraFilter: `OrganizationID = '${org.ID}'`,
        ResultType: 'entity_object',
      });
      const existingSub = (subRes.Success ? subRes.Results : [])[0];
      const sub = existingSub ?? await md.GetEntityObject<BCSaaSSubscriptionEntity>('BC: Subscriptions');
      if (!existingSub) {
        sub.NewRecord();
        sub.OrganizationID = org.ID;
        sub.StartDate = new Date();
      }
      sub.PlanPriceID = planPriceId;
      sub.Status = 'Active';
      if (!await sub.Save()) {
        throw new Error(`Failed to save Subscription: ${sub.LatestResult?.Message ?? 'unknown'}`);
      }

      // 3. Set OrganizationConfig.PlanID (the second resolution path).
      const ocRes = await rv.RunView<BCSaaSOrganizationConfigEntity>({
        EntityName: 'BC: Organization Configs',
        ExtraFilter: `OrganizationID = '${org.ID}'`,
        ResultType: 'entity_object',
      });
      const existingOC = (ocRes.Success ? ocRes.Results : [])[0];
      const oc = existingOC ?? await md.GetEntityObject<BCSaaSOrganizationConfigEntity>('BC: Organization Configs');
      if (!existingOC) {
        oc.NewRecord();
        oc.OrganizationID = org.ID;
      }
      oc.PlanID = planId;
      if (!await oc.Save()) {
        throw new Error(`Failed to save OrganizationConfig: ${oc.LatestResult?.Message ?? 'unknown'}`);
      }

      await this.loadCurrentPlan(org.ID);
      this.planMessage = `Plan set to ${this.currentPlan?.Name ?? 'selected plan'}.`;
    } catch (e) {
      console.error('Organizations: setPlan failed', e);
      this.planMessage = `Failed to set plan: ${e instanceof Error ? e.message : String(e)}`;
    } finally {
      this.isSavingPlan = false;
    }
  }

  // ========== Filtered & Sorted Organizations ==========

  get displayOrganizations(): mjBizAppsCommonOrganizationEntity[] {
    let result = [...this.organizations];

    // Apply search filter
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      result = result.filter(o =>
        o.Name.toLowerCase().includes(query) ||
        (o.Description?.toLowerCase().includes(query))
      );
    }

    // Apply sort
    result.sort((a, b) => {
      let comparison = 0;
      if (this.sortBy === 'name') {
        comparison = a.Name.localeCompare(b.Name);
      } else if (this.sortBy === 'status') {
        comparison = (a.Status || '').localeCompare(b.Status || '');
      }
      return this.sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }

  // ========== Sort Controls ==========

  setSortBy(field: 'name' | 'status'): void {
    if (this.sortBy === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = field;
      this.sortDirection = 'asc';
    }
  }

  getSortIcon(field: string): string {
    if (this.sortBy !== field) return 'fa-sort';
    return this.sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
  }

  // ========== CRUD Operations ==========

  startCreate(parentOrg?: mjBizAppsCommonOrganizationEntity): void {
    this.isCreating = true;
    this.isEditing = false;
    this.selectedOrganization = null;
    this.editForm = {
      name: '',
      description: '',
      parentId: parentOrg?.ID || null,
      logoURL: null
    };
    this.logoPreviewError = false;
  }

  /**
   * Start creating a sub-organization under the selected or specified org
   */
  startCreateSubOrg(parentOrg?: mjBizAppsCommonOrganizationEntity): void {
    const parent = parentOrg || this.selectedOrganization;
    if (!parent) {
      this.startCreate();
      return;
    }
    this.startCreate(parent);
  }

  startEdit(): void {
    if (!this.selectedOrganization) return;
    this.isEditing = true;
    this.isCreating = false;
    this.editForm = {
      name: this.selectedOrganization.Name,
      description: this.selectedOrganization.Description || '',
      parentId: this.selectedOrganization.ParentID || null,
      logoURL: this.selectedOrganization.LogoURL || null
    };
    this.logoPreviewError = false;
  }

  cancelEdit(): void {
    this.isEditing = false;
    this.isCreating = false;
    this.editForm = { name: '', description: '', parentId: null, logoURL: null };
    this.logoPreviewError = false;
  }

  async saveOrganization(): Promise<void> {
    if (!this.editForm.name.trim()) {
      this.errorMessage = 'Organization name is required.';
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';

    try {
      const md = new Metadata();

      if (this.isCreating) {
        // Create new organization
        const newOrg = await md.GetEntityObject<OrganizationEntityExtended>('MJ_BizApps_Common: Organizations');
        newOrg.Name = this.editForm.name.trim();
        newOrg.Description = this.editForm.description.trim() || null;
        newOrg.ParentID = this.editForm.parentId || null;
        newOrg.LogoURL = this.editForm.logoURL?.trim() || null;
        await newOrg.Save();

        // Auto-add the creator as an Owner member (v1.3 BAC + OrganizationRole)
        const currentPersonId = IzzyMetadataEngine.Instance.CurrentContactID;
        if (currentPersonId) {
          const roleRv = new RunView();
          const roleResult = await roleRv.RunView<BCSaaSOrganizationRoleEntity>({
            EntityName: 'BC: Organization Roles',
            ExtraFilter: "DisplayName = 'Owner'"
          });
          if (roleResult.Success && roleResult.Results.length > 0) {
            await BCMembershipService.CreateMembership(
              currentPersonId,
              newOrg.ID,
              roleResult.Results[0].ID,
              Metadata.Provider.CurrentUser
            );
          }
        }

        // Refresh the engine cache since we added a new record
        await IzzyMetadataEngine.Instance.Config(true);
        this.organizations = IzzyMetadataEngine.Instance.GetCurrentUserOrganizations();
        this.buildOrgTree();

        const parentName = this.editForm.parentId
          ? this.organizations.find(o => o.ID === this.editForm.parentId)?.Name
          : null;
        this.showSuccess(parentName ? `Sub-organization created under ${parentName}` : 'Organization created');

        // Select the newly created org
        const createdOrg = this.organizations.find(o => o.Name === this.editForm.name.trim());
        if (createdOrg) {
          this.selectOrganization(createdOrg);
        }
      } else if (this.isEditing && this.selectedOrganization) {
        // Update existing organization
        this.selectedOrganization.Name = this.editForm.name.trim();
        this.selectedOrganization.Description = this.editForm.description.trim() || null;
        this.selectedOrganization.ParentID = this.editForm.parentId || null;
        this.selectedOrganization.LogoURL = this.editForm.logoURL?.trim() || null;
        await this.selectedOrganization.Save();

        // Rebuild tree since parent may have changed
        this.buildOrgTree();

        this.showSuccess('Organization updated');
      }

      // Notify parent that organizations changed
      this.organizationsChanged.emit();

      this.isEditing = false;
      this.isCreating = false;
    } catch (error) {
      console.error('Error saving organization:', error);
      this.errorMessage = 'Failed to save organization. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  deleteOrganization(): void {
    if (!this.selectedOrganization) return;

    const orgToDelete = this.selectedOrganization;

    // Check if org has children
    const hasChildren = this.organizations.some(o => o.ParentID === orgToDelete.ID);
    const warningMsg = hasChildren
      ? `Are you sure you want to delete "${orgToDelete.Name}"? This organization has sub-organizations that will become top-level. All organization settings will also be deleted.`
      : `Are you sure you want to delete "${orgToDelete.Name}"? This will also delete all organization settings.`;

    this.showConfirmDialog(
      'Delete Organization',
      warningMsg,
      async () => {
        this.isSaving = true;
        try {
          // Load the entity object from database (cache has plain objects without methods)
          const rv = new RunView();
          const orgResult = await rv.RunView<mjBizAppsCommonOrganizationEntity>({
            EntityName: 'MJ_BizApps_Common: Organizations',
            ExtraFilter: `ID = '${orgToDelete.ID}'`,
            ResultType: 'entity_object'
          });

          if (!orgResult.Success || orgResult.Results.length === 0) {
            this.errorMessage = 'Organization not found.';
            return;
          }

          const orgEntity = orgResult.Results[0];

          // Cascade-delete related records that block org deletion
          // NOTE: stopgap. Only clears a subset of the org's ~23 FK children; OPR/member
          // cleanup is handled server-side by BCSaaSOrganizationEntityServer. Orgs with
          // Channels/Subscriptions/API keys will still fail to delete (FK conflict) until a
          // proper org-delete design (guard vs. full cascade) is decided. See session notes.
          const relatedEntities = ['Organization Actions', 'BC: Organization Person Roles', 'BC: Organization Settings'];
          for (const entityName of relatedEntities) {
            const related = await rv.RunView({
              EntityName: entityName,
              ExtraFilter: `OrganizationID = '${orgToDelete.ID}'`,
              ResultType: 'entity_object'
            });
            if (related.Success && related.Results.length > 0) {
              for (const record of related.Results) {
                await record.Delete();
              }
            }
          }

          const deleteResult = await orgEntity.Delete();

          if (!deleteResult) {
            this.errorMessage = 'Cannot delete organization. It may still have channels or other linked records.';
            return;
          }

          // Refresh the engine cache since we deleted a record
          await IzzyMetadataEngine.Instance.Config(true);
          this.organizations = IzzyMetadataEngine.Instance.GetCurrentUserOrganizations();
          this.buildOrgTree();

          this.showSuccess('Organization deleted');
          this.selectedOrganization = null;

          // Notify parent that organizations changed
          this.organizationsChanged.emit();

          // Auto-select first root org if available
          if (this.orgTree.length > 0) {
            this.selectOrganization(this.orgTree[0].org);
          } else if (this.organizations.length > 0) {
            this.selectOrganization(this.organizations[0]);
          }
        } catch (error) {
          console.error('Error deleting organization:', error);
          this.errorMessage = 'Failed to delete organization. Please remove all members and channels first.';
        } finally {
          this.isSaving = false;
        }
      }
    );
  }

  // ========== Confirm Dialog Methods ==========

  protected showConfirmDialog(title: string, message: string, action: () => Promise<void>): void {
    this.confirmDialogTitle = title;
    this.confirmDialogMessage = message;
    this.confirmDialogAction = action;
    this.confirmDialogVisible = true;
  }

  protected async confirmDialogYes(): Promise<void> {
    if (this.confirmDialogAction) {
      await this.confirmDialogAction();
    }
    this.closeConfirmDialog();
  }

  protected closeConfirmDialog(): void {
    this.confirmDialogVisible = false;
    this.confirmDialogAction = null;
  }

  // ========== Navigation ==========

  openOrganizationRecord(org?: mjBizAppsCommonOrganizationEntity): void {
    const organization = org || this.selectedOrganization;
    if (!organization) return;

    // Use NavigationService to open the entity record
    this.navigationService.OpenEntityRecord(
      'MJ_BizApps_Common: Organizations',
      CompositeKey.FromID(organization.ID)
    );
  }

  navigateToSettings(org?: mjBizAppsCommonOrganizationEntity): void {
    const organization = org || this.selectedOrganization;
    if (!organization) return;

    // Emit event for parent components (e.g., Administration panel)
    // If parent handles it, the navigation will be done in-context
    this.settingsNavigation.emit(organization.ID);

    // Fallback: Use NavigationService for standalone usage
    // (only triggers if not embedded in Administration panel)
    if (this.settingsNavigation.observers.length === 0) {
      this.navigationService.OpenNavItemByName('Settings', {
        organizationId: organization.ID
      });
    }
  }

  // ========== Utilities ==========

  getInitials(name: string): string {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  /**
   * Get parent organization name for display
   */
  getParentName(org: mjBizAppsCommonOrganizationEntity): string | null {
    const parentId = org.ParentID;
    if (!parentId) return null;
    return this.organizations.find(o => o.ID === parentId)?.Name || null;
  }

  /**
   * Get channel count for an organization
   */
  getChannelCount(org: mjBizAppsCommonOrganizationEntity): number {
    return IzzyMetadataEngine.Instance.Channels?.filter(c => c.OrganizationID === org.ID).length || 0;
  }

  /**
   * Get parent org name by ID (for create form display)
   */
  getParentOrgName(parentId: string | null): string {
    if (!parentId) return '';
    return this.organizations.find(o => o.ID === parentId)?.Name || '';
  }

  /**
   * Get the hierarchy path as a display string (e.g., "Parent → Child → Grandchild")
   */
  getOrgPathString(org: mjBizAppsCommonOrganizationEntity): string {
    return this.getOrgPath(org).map(o => o.Name).join(' → ');
  }

  /**
   * Navigate to the parent organization of the selected org
   */
  navigateToParent(): void {
    if (!this.selectedOrganization) return;
    const parentId = this.selectedOrganization.ParentID;
    if (!parentId) return;
    const parent = this.organizations.find(o => o.ID === parentId);
    if (parent) {
      this.selectOrganization(parent);
    }
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => {
      this.successMessage = '';
    }, 3000);
  }

  // ========== Logo Handling ==========

  /**
   * Get the logo URL for an organization (for display)
   */
  getLogoURL(org: mjBizAppsCommonOrganizationEntity): string | null {
    return org.LogoURL || null;
  }

  /**
   * Handle file selection for logo upload
   */
  onLogoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    // Validate file type
    if (!file.type.startsWith('image/')) {
      this.errorMessage = 'Please select an image file (PNG, JPG, GIF, etc.)';
      return;
    }

    // Validate file size (max 500KB for base64 storage)
    const maxSize = 500 * 1024; // 500KB
    if (file.size > maxSize) {
      this.errorMessage = 'Image must be smaller than 500KB for embedded storage. Consider using a URL instead.';
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = () => {
      this.editForm.logoURL = reader.result as string;
      this.logoPreviewError = false;
    };
    reader.onerror = () => {
      this.errorMessage = 'Failed to read image file';
    };
    reader.readAsDataURL(file);
  }

  /**
   * Clear the logo URL
   */
  clearLogo(): void {
    this.editForm.logoURL = null;
    this.logoPreviewError = false;
  }

  /**
   * Handle logo preview load error
   */
  onLogoPreviewError(): void {
    this.logoPreviewError = true;
  }

  /**
   * Check if the logo URL is a base64 data URL
   */
  isBase64Logo(url: string | null): boolean {
    return url?.startsWith('data:image/') || false;
  }
}

/**
 * Utility function to prevent tree-shaking
 */
export function LoadOrganizationsComponent(): void {
  // Intentionally empty - prevents tree-shaking
}
